import { DefaultMantineColor, DEFAULT_THEME } from "@mantine/core"
import { Application, Qualifies } from "@prisma/client"

export const qualificationFields = [
  "planType",
  "planReason",
  "planLocation",
  "liraProvince",
] as const
export type QualificationField = typeof qualificationFields[number]

const nonQualifyingPlanTypes = new Set([
  "lif", // one reason
  "rrsp", // one reason
  "cpp",
  "oas",
  "disability",
  "annuity",
  "insurance-settlement",
])

export const getFieldQualification = (
  field: QualificationField,
  value?: string | null
): Qualifies => {
  if (!value) return "NO"

  if (field === "planType" && nonQualifyingPlanTypes.has(value)) return "NO"
  if (field === "planLocation" && value === "government") return "NO"

  if (field === "liraProvince" && (value === "SK" || value == "QC")) return "NO"
  if (value === "not-sure" || value === "other") return "NEED_MORE_INFO"

  return "YES"
}

type QualificationResultField = {
  id: QualificationField
  qualifies: Qualifies
}

export type QualificationResult =
  | {
      qualifies: "NO" | "NEED_MORE_INFO"
      fields: QualificationResultField[]
    }
  | {
      qualifies: "YES"
    }

export const getQualification = (
  values: Pick<Application, QualificationField>
): QualificationResult => {
  const fields: QualificationResultField[] = qualificationFields.map((field) => ({
    id: field,
    qualifies: getFieldQualification(field, values[field]),
  }))

  const notQualifyingFields = fields.filter((field) => field.qualifies === "NO")
  if (notQualifyingFields.length > 0) return { qualifies: "NO", fields: notQualifyingFields }

  const needMoreInfoFields = fields.filter((field) => field.qualifies === "NEED_MORE_INFO")
  if (needMoreInfoFields.length > 0)
    return { qualifies: "NEED_MORE_INFO", fields: needMoreInfoFields }

  return { qualifies: "YES" }
}

export const getQualificationColor = (qualification: Qualifies): DefaultMantineColor => {
  if (qualification === "NO") return "red"
  if (qualification === "NEED_MORE_INFO") return "yellow"
  return "green"
}

export const getQualificationEmoji = (qualification: Qualifies) => {
  if (qualification === "NO") return "🔴"
  if (qualification === "NEED_MORE_INFO") return "🟡"
  return "🟢"
}

export const getQualificationCaption = (qualification: Qualifies) => {
  if (qualification === "NO") return "Doesn't qualify"
  if (qualification === "NEED_MORE_INFO") return "Need more info"
  return "Qualifies"
}
